import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../../firebase_config";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import { DUMMY_PROJECTS } from "../publish/dialogs/ArchitectProfileDialog";
import EnquiryDialog from "../publish/dialogs/EnquiryDialog";
import "./solutions.css";
import "./borderless.css";
import "./borderless-portfolio.css";

const API_BASE = process.env.REACT_APP_API_BASE;
// cover_images come as /v1/media/... paths; API_BASE ends with /v1
const MEDIA_BASE = (API_BASE ?? "").replace(/\/v1\/?$/, "");

function mediaUrl(path) {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${MEDIA_BASE}${path}`;
}

function normalizeGeoFeature(feature) {
    const p = feature.properties ?? {};
    const firstCover = p.cover_images?.[0] ?? p.cover ?? p.image ?? null;
    return {
        id: feature.id ?? p.code,
        name: p.name,
        description: p.description,
        image: mediaUrl(firstCover),
        cover_images: (p.cover_images ?? []).map(mediaUrl),
        code: p.code,
        company: p.company_name || null,
        status: p.is_ongoing ? "Ongoing" : "Completed",
        expected_start_date: p.expected_start_date ?? null,
        partners: p.partners ?? [],
    };
}

function normalizeProjects(raw) {
    if (!raw) return [];
    // Top-level FeatureCollection
    if (raw.type === "FeatureCollection" && Array.isArray(raw.features)) {
        return raw.features.map(normalizeGeoFeature);
    }
    // { results: { type: "FeatureCollection", features: [...] } }
    if (raw.results?.type === "FeatureCollection" && Array.isArray(raw.results.features)) {
        return raw.results.features.map(normalizeGeoFeature);
    }
    // { results: [...features] }
    if (Array.isArray(raw.results)) {
        const arr = raw.results;
        return arr[0]?.type === "Feature" ? arr.map(normalizeGeoFeature) : arr;
    }
    // Plain array of features
    if (Array.isArray(raw)) {
        return raw[0]?.type === "Feature" ? raw.map(normalizeGeoFeature) : raw;
    }
    return [];
}

const SAMPLE_PROFILE = {
    name: "Amara Okonkwo",
    initials: "AO",
    id: "A408291",
    flag: "🇰🇪",
    country: "Kenya",
    board: "BORAQS",
    professional_type: "architect",
};

function getInitials(name = "") {
    return name.trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function ProfileHero({ profile, projects, isSample }) {
    const completed = projects.filter(p => p.status === "Completed").length;
    const ongoing = projects.filter(p => p.status === "Ongoing").length;
    const [enquiryOpen, setEnquiryOpen] = useState(false);

    const handleSend = async ({ email, message }) => {
        try {
            const sendEmailFn = httpsCallable(getFunctions(app), "sendEmail");
            await sendEmailFn({
                subject: `Portfolio Enquiry: ${profile.name || profile.registration_no}`,
                html: `<p><strong>From:</strong> ${email}</p><p>${message}</p>`,
                replyTo: email,
            });
        } catch (err) {
            console.error("Failed to send enquiry:", err);
        }
        setEnquiryOpen(false);
    };

    return (
        <>
        <div className="brdp-hero">
            <div className="brdp-hero-glow" aria-hidden="true" />
            {isSample && (
                <div className="brdp-sample-banner">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
                    </svg>
                    Sample portfolio — showing dummy data
                </div>
            )}
            <div className="brdp-hero-inner">
                <div className="brdp-profile-row">
                    <div className="brdp-avatar">{profile.initials || getInitials(profile.name || profile.registration_no || "")}</div>
                    <div className="brdp-profile-info">
                        <div className="brdp-name-row">
                            <h1 className="brdp-name">{profile.name || profile.registration_no}</h1>
                            <span className="brdp-verified-badge">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#77ffd8" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                                    <path d="m9 12 2 2 4-4" />
                                </svg>
                                Verified {profile.professional_type === "firm" ? "Firm" : "Architect"}
                            </span>
                        </div>
                        <p className="brdp-profile-meta">
                            {profile.flag && <>{profile.flag} </>}
                            {profile.country || profile.area_of_operation}
                            {profile.board && <> &middot; {profile.board}</>}
                            {" "}&middot; Registry ID <strong className="brdp-profile-id">{profile.id || profile.registration_no}</strong>
                        </p>
                    </div>
                    <button className="brdp-cta-btn" onClick={() => setEnquiryOpen(true)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        Enquire
                    </button>
                </div>
                <div className="brdp-stats-row">
                    <div className="brdp-stat">
                        <span className="brdp-stat-val">{projects.length}</span>
                        <span className="brdp-stat-label">Projects</span>
                    </div>
                    <div className="brdp-stat">
                        <span className="brdp-stat-val">{completed}</span>
                        <span className="brdp-stat-label">Completed</span>
                    </div>
                    <div className="brdp-stat">
                        <span className="brdp-stat-val">{ongoing}</span>
                        <span className="brdp-stat-label">Ongoing</span>
                    </div>
                </div>
            </div>
        </div>
        <EnquiryDialog
            isOpen={enquiryOpen}
            onClose={() => setEnquiryOpen(false)}
            project={{ name: profile.name || profile.registration_no }}
            breadcrumb={["PORTFOLIO", "ENQUIRE"]}
            onSend={handleSend}
        />
        </>
    );
}

function ProjectGrid({ projects }) {
    if (projects.length === 0) {
        return (
            <div className="brdp-empty">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                </svg>
                <p>No projects on this portfolio yet.</p>
            </div>
        );
    }
    return (
        <div className="brdp-grid">
            {projects.map((p, i) => {
                const bannerSrc = p.image ?? p.cover_images?.[0] ?? null;
                return (
                    <div key={p.id ?? i} className="brdp-card">
                        <div className="brdp-card-banner">
                            {bannerSrc
                                ? <img src={bannerSrc} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : null}
                        </div>
                        <div className="brdp-card-body">
                            <h3 className="brdp-card-name">{p.name}</h3>
                            {p.description && (
                                <p className="brdp-card-desc">{p.description}</p>
                            )}
                            {p.location && (
                                <div className="brdp-card-location">
                                    <span className="brdp-card-city">{p.location}</span>
                                </div>
                            )}
                            <div className="brdp-card-company-row">
                                {p.company && <span className="brdp-card-company">{p.company}</span>}
                                {p.code && <span className="brdp-card-code">{p.code}</span>}
                            </div>
                            <div className="brdp-card-footer">
                                <span className={`brdp-card-role${p.status === "Completed" ? " brdp-card-role--done" : ""}`}>{p.status}</span>
                                {p.expected_start_date && (
                                    <span className="brdp-card-date">
                                        {new Date(p.expected_start_date).toLocaleDateString("en-GB", { year: "numeric", month: "short" })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function SamplePortfolio({ navigate }) {
    return (
        <>
            <ProfileHero profile={SAMPLE_PROFILE} projects={DUMMY_PROJECTS} isSample />
            <div className="brdp-body">
                <div className="brdp-grid-header">
                    <div>
                        <h2 className="brdp-grid-title">Global Portfolio</h2>
                        <p className="brdp-grid-sub">
                            When a contractor or developer adds you to a project on ConRa, it appears here instantly — verified and yours, wherever the work is.
                        </p>
                    </div>
                </div>
                <ProjectGrid projects={DUMMY_PROJECTS} />
            </div>
        </>
    );
}

function ProfileHeroSkeleton() {
    return (
        <div className="brdp-hero">
            <div className="brdp-hero-glow" aria-hidden="true" />
            <div className="brdp-hero-inner">
                <div className="brdp-profile-row">
                    <div className="brdp-avatar brdp-sk-dark" style={{ background: "none" }} />
                    <div className="brdp-profile-info">
                        <div className="brdp-sk-dark" style={{ width: 220, height: 30, borderRadius: 8, marginBottom: 10 }} />
                        <div className="brdp-sk-dark" style={{ width: 150, height: 16, borderRadius: 6 }} />
                    </div>
                </div>
                <div className="brdp-stats-row">
                    {[80, 90, 80].map((w, i) => (
                        <div key={i} className="brdp-stat">
                            <div className="brdp-sk-dark" style={{ width: w, height: 32, borderRadius: 6, marginBottom: 8 }} />
                            <div className="brdp-sk-dark" style={{ width: 60, height: 13, borderRadius: 5 }} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ProfileHeroError({ registrationNo, error }) {
    return (
        <div className="brdp-hero">
            <div className="brdp-hero-glow" aria-hidden="true" />
            <div className="brdp-hero-inner">
                <div className="brdp-profile-row">
                    <div className="brdp-avatar" style={{ opacity: 0.35 }}>?</div>
                    <div className="brdp-profile-info">
                        <div className="brdp-name-row">
                            <h1 className="brdp-name" style={{ opacity: 0.55 }}>{registrationNo.toUpperCase()}</h1>
                        </div>
                        <p className="brdp-profile-meta" style={{ color: "#fca5a5" }}>{error}</p>
                    </div>
                </div>
                <div className="brdp-stats-row">
                    {["Projects", "Completed", "Ongoing"].map(label => (
                        <div key={label} className="brdp-stat">
                            <span className="brdp-stat-val" style={{ opacity: 0.35 }}>—</span>
                            <span className="brdp-stat-label">{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ProjectGridSkeleton() {
    return (
        <div className="brdp-grid">
            {[1, 2, 3].map(i => (
                <div key={i} className="brdp-card">
                    <div className="brdp-card-banner brdp-sk-light" />
                    <div className="brdp-card-body">
                        <div className="brdp-sk-light" style={{ width: "65%", height: 18, borderRadius: 6, marginBottom: 10 }} />
                        <div className="brdp-sk-light" style={{ width: "90%", height: 13, borderRadius: 5, marginBottom: 6 }} />
                        <div className="brdp-sk-light" style={{ width: "55%", height: 13, borderRadius: 5, marginBottom: 20 }} />
                        <div className="brdp-sk-light" style={{ width: "40%", height: 22, borderRadius: 20 }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

function LivePortfolio({ registrationNo }) {
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState("");

    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [projectsError, setProjectsError] = useState("");

    const regNo = registrationNo.toUpperCase();

    // Fetch profile independently
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setProfileLoading(true);
            setProfileError("");
            try {
                const res = await fetch(
                    `${API_BASE}/auth/user/add_professional_to_project/?limit=10&offset=0&search=${encodeURIComponent(regNo)}`
                );
                if (!res.ok) throw new Error(`Could not load profile (${res.status})`);
                const data = await res.json();
                const results = data?.results ?? (Array.isArray(data) ? data : []);
                if (results.length === 0) throw new Error("No professional found with that registration number.");
                const raw = results[0];
                if (!cancelled) {
                    setProfile({
                        name: raw.name,
                        registration_no: raw.professional_id,
                        area_of_operation: raw.area_of_operation,
                        professional_type: raw.professional_type,
                    });
                }
            } catch (err) {
                if (!cancelled) setProfileError(err.message || "Failed to load profile.");
            } finally {
                if (!cancelled) setProfileLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [regNo]);

    // Fetch projects independently — wait for profile to resolve first
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (profileLoading) return;
            setProjectsLoading(true);
            setProjectsError("");
            try {
                // Use the returned professional_type; fall back to "architect" only when the profile endpoint failed
                const type = profileError ? "architect" : (profile?.professional_type ?? "architect");
                const res = await fetch(
                    `${API_BASE}/auth/user/professional/?limit=10&offset=0&professional_type=${encodeURIComponent(type)}&registration_no=${encodeURIComponent(regNo)}`
                );
                if (!res.ok) throw new Error(`Could not load projects (${res.status})`);
                const data = await res.json();
                if (!cancelled) setProjects(normalizeProjects(data));
            } catch (err) {
                if (!cancelled) setProjectsError(err.message || "Failed to load projects.");
            } finally {
                if (!cancelled) setProjectsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [regNo, profileLoading, profile?.professional_type, profileError]);

    return (
        <>
            {profileLoading
                ? <ProfileHeroSkeleton />
                : profile
                    ? <ProfileHero profile={profile} projects={projects} isSample={false} />
                    : <ProfileHeroError registrationNo={regNo} error={profileError} />
            }
            <div className="brdp-body">
                <div className="brdp-grid-header">
                    <div>
                        <h2 className="brdp-grid-title">Global Portfolio</h2>
                        <p className="brdp-grid-sub">
                            {profile
                                ? <>Verified projects linked to registration number <strong>{regNo}</strong>.</>
                                : profileLoading
                                    ? "Loading portfolio…"
                                    : "Verified projects will appear here."}
                        </p>
                    </div>
                </div>
                {projectsLoading
                    ? <ProjectGridSkeleton />
                    : projectsError
                        ? (
                            <div className="brdp-empty">
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#e5a000" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <path d="M12 9v4M12 17h.01" />
                                </svg>
                                <p>{projectsError}</p>
                            </div>
                        )
                        : <ProjectGrid projects={projects} />
                }
            </div>
        </>
    );
}

export default function BorderlessPortfolioPage() {
    const { registrationNo } = useParams();
    const navigate = useNavigate();
    const isLive = Boolean(registrationNo);

    if (isLive) {
        return (
            <div className="brdp-root">
                <LivePortfolio registrationNo={registrationNo} />
            </div>
        );
    }

    return (
        <div>
            <div className="web-only"><HeaderAny /></div>
            <div className="mobile-only"><MobileNav /></div>

            <div className="brdp-root">
                <button className="brdp-back-btn" onClick={() => navigate("/solutions/borderless#register")}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                    Back to registration
                </button>

                <SamplePortfolio navigate={navigate} />
            </div>

            <Footer />
        </div>
    );
}
